import { Component, Inject, OnInit, Optional } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { AccountIdentity, AuthService } from '../../services/auth.service';
import { ChangePasswordDialogComponent } from '../account/change-password-dialog.component';

export type ConfigurationSection = 'account' | 'users' | 'roles' | 'apiKeys';

/** Which section to land on. Absent means the first one, which is how the
 *  account menu opens it; a caller sending somebody here to do one thing names
 *  that thing's section instead. */
export interface ConfigurationDialogData {
  section?: ConfigurationSection;
}

/**
 * How this dialog is presented, in one place.
 *
 * Two callers open it - the account menu and the "add one in Services" link in
 * the engine picker - and a dialog that is 80vw from one of them and something
 * else from the other is the same dialog wearing two sizes. Not a helper: a
 * literal that has to agree in two files, named where the component it
 * describes lives.
 */
export const CONFIGURATION_DIALOG_CONFIG: MatDialogConfig<ConfigurationDialogData> = {
  width: '80vw',
  maxWidth: '1100px',
  panelClass: 'configuration-dialog-panel',
};

/** Open it, optionally on a named section. */
export function openConfigurationDialog(
  dialog: MatDialog,
  section?: ConfigurationSection,
): void {
  dialog.open(ConfigurationDialogComponent, {
    ...CONFIGURATION_DIALOG_CONFIG,
    data: { section },
  });
}

/**
 * Account-level Configuration, shown as a modal rather than a routed page.
 * Hosts the configuration components directly via an internal
 * section switch — no router, so it never touches PermissionGuard. The list
 * components own their own nested dialogs (create/edit/detail), so this shell
 * stays thin.
 */
@Component({
  selector: 'app-configuration-dialog',
  templateUrl: './configuration-dialog.component.html',
  styleUrls: ['./configuration-dialog.component.css', '../configuration-list.css'],
})
export class ConfigurationDialogComponent implements OnInit {
  section: ConfigurationSection;
  account: AccountIdentity | null = null;
  accountLoading = true;
  accountError = false;

  constructor(
    private auth: AuthService,
    private dialog: MatDialog,
    // Optional because the account menu opens this with no data at all, and a
    // required token would make that call site throw rather than default.
    @Optional() @Inject(MAT_DIALOG_DATA) data: ConfigurationDialogData | null,
  ) {
    this.section = data?.section ?? 'account';
  }

  openChangePassword(): void {
    this.dialog.open(ChangePasswordDialogComponent, {
      width: '440px',
      maxWidth: '92vw',
    });
  }

  // `requires` gates a nav item on a permission; null = always shown.
  //
  // Every label here is the heading of the panel it opens, because the nav is
  // read as a list of places and a place should not be called two things. The
  // last one was "API Keys" against a panel headed "Services" - and the panel
  // is the honest name: what it holds is the external services this account
  // uses, of which a key is how you connect to some of them. Agent seats are
  // in there too, and they have no key at all.
  //
  // The id stays `apiKeys`: it is a switch value in this component's template,
  // it is not written anywhere a person can see, and renaming it would touch
  // three files to say nothing new.
  private readonly allSections: { id: ConfigurationSection; label: string; icon: string; requires: string | null }[] = [
    { id: 'account',  label: 'Account',  icon: 'person',   requires: null },
    { id: 'users',    label: 'Users',    icon: 'people',  requires: null },
    { id: 'roles',    label: 'Roles',    icon: 'badge',   requires: null },
    { id: 'apiKeys',  label: 'Services', icon: 'vpn_key', requires: null },
  ];

  get sections() {
    return this.allSections.filter(s => !s.requires || this.auth.hasPermission(s.requires));
  }

  ngOnInit(): void {
    this.loadAccount();
  }

  loadAccount(): void {
    this.accountLoading = true;
    this.accountError = false;
    this.auth.getAccountIdentity().subscribe({
      next: account => {
        this.account = account;
        this.accountLoading = false;
      },
      error: () => {
        this.account = null;
        this.accountLoading = false;
        this.accountError = true;
      },
    });
  }
}
